import type { QueryClient } from '@tanstack/react-query'
import type { Persister } from '@tanstack/react-query-persist-client'
import { reportError } from '@/lib/errorReporter'

// The auth store (a plain Zustand module) must not import main.tsx, so
// main.tsx hands the query layer over here once at startup and logout calls
// clearCachesForLogout() through this indirection.
let queryClient: QueryClient | null = null
let persister: Persister | null = null

export function initCacheReset(client: QueryClient, clientPersister: Persister): void {
  queryClient = client
  persister = clientPersister
}

/**
 * Wipes user-scoped caches on logout so the next login on a shared browser
 * can't see the previous user's data:
 * - the in-memory query + mutation caches (also drops queued mutations),
 * - the IDB-persisted query client (maxAge: Infinity — it never expires),
 * - the service worker's `api-cache` (NetworkFirst copies of authed GETs).
 *
 * The public `bulk-product-data` and `card-images` SW caches are not
 * user-scoped and are deliberately kept.
 *
 * Fire-and-forget: logout must never block on cache cleanup.
 */
export function clearCachesForLogout(): void {
  try {
    queryClient?.clear()
  } catch (error) {
    reportError(error, { context: 'cacheReset.queryClient.clear' })
  }
  void Promise.resolve(persister?.removeClient()).catch((error) =>
    reportError(error, { context: 'cacheReset.persister.removeClient' }),
  )
  if (typeof caches !== 'undefined') {
    caches.delete('api-cache').catch(() => {
      // CacheStorage unavailable (insecure context) — nothing to clean up.
    })
  }
}
